import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { UpdateUserRequest, UserFullData } from "../../entry/server/user.ts";
import { userRepo } from "../../repo/user-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { toTehranDateTimeString } from "../../utils/common_helpers.ts";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import NotificationSendForm from "../../componrnts/notification-send-form.tsx";

const inputClass =
  "w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400";

const ROLE_OPTIONS = [
  { label: "ادمین", value: "admin" },
  { label: "کاربر", value: "user" },
];

function normalizeUser(data: UserFullData): UserFullData {
  return {
    id: data.id,
    username: data.username ?? "",
    name: data.name ?? "",
    email: data.email ?? "",
    phone: data.phone ?? "",
    role: data.role ?? "",
    vip_start_date: data.vip_start_date ?? "",
    vip_end_date: data.vip_end_date ?? "",
    user_device_data: data.user_device_data ?? [],
    score: data.score ?? 0,
    vip_score: data.vip_score ?? 0,
    level: data.level ?? 0,
  };
}

function toUpdateForm(u: UserFullData): UpdateUserRequest {
  return {
    name: u.name ?? "",
    email: u.email ?? "",
    phone: u.phone ?? "",
    role: u.role ?? "",
    password: "",
    vip_start_date: u.vip_start_date ?? "",
    vip_end_date: u.vip_end_date ?? "",
    score: u.score ?? 0,
    vip_score: u.vip_score ?? 0,
    level: u.level ?? 0,
  };
}

/** مقدار اولیهٔ DatePicker شمسی از رشتهٔ سرور (ایزو یا شمسی) */
function parseVipDateString(raw: string): DateObject | null {
  if (!raw?.trim()) return null;
  const s = raw.trim();
  const fromIso = new Date(s);
  if (!Number.isNaN(fromIso.getTime())) {
    return new DateObject({ date: fromIso, calendar: persian, locale: persian_fa });
  }
  try {
    return new DateObject({ date: s, calendar: persian, locale: persian_fa });
  } catch {
    return null;
  }
}

export default function UserDetail() {
  const { id } = useParams();
  const userId = Number(id);
  const navigate = useNavigate();
  const { setMessage } = useContext(AppContext);
  const [user, setUser] = useState<UserFullData | null>(null);
  const [form, setForm] = useState<UpdateUserRequest | null>(null);
  const [vipStartAt, setVipStartAt] = useState<DateObject | null>(null);
  const [vipEndAt, setVipEndAt] = useState<DateObject | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const applyUserData = (normalized: UserFullData) => {
    setUser(normalized);
    setForm(toUpdateForm(normalized));
    setVipStartAt(parseVipDateString(normalized.vip_start_date));
    setVipEndAt(parseVipDateString(normalized.vip_end_date));
  };

  useEffect(() => {
    if (!userId) {
      setMessage({ type: "error", message: "شناسه کاربر معتبر نیست." });
      navigate("/admin/users");
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const res = await userRepo.getUser(userId);
        if (!res.status || !res.data) {
          setMessage({ type: "error", message: res.message || "دریافت اطلاعات کاربر ناموفق بود." });
          navigate("/admin/users");
          return;
        }
        const normalized = normalizeUser(res.data);
        applyUserData(normalized);
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
        navigate("/admin/users");
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [userId, navigate, setMessage]);

  const reloadUser = async () => {
    const res = await userRepo.getUser(userId);
    if (res.status && res.data) {
      applyUserData(normalizeUser(res.data));
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    try {
      const res = await userRepo.updateUser(userId, form);
      if (!res.status) {
        setMessage({ type: "error", message: res.message || "ذخیره تغییرات ناموفق بود." });
        return;
      }
      setMessage({ type: "success", message: "تغییرات با موفقیت ذخیره شد." });
      await reloadUser();
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setSaving(false);
    }
  };

  const onDeleteUser = async () => {
    if (!window.confirm("این کاربر حذف شود؟ این عمل قابل بازگشت نیست.")) return;
    try {
      const res = await userRepo.deleteUser(userId);
      if (!res.status) {
        setMessage({ type: "error", message: res.message || "حذف کاربر ناموفق بود." });
        return;
      }
      setMessage({ type: "success", message: "کاربر حذف شد." });
      navigate("/admin/users");
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    }
  };

  const onDeleteDevice = async (token: string) => {
    if (!window.confirm("این دستگاه از حساب کاربر جدا شود؟")) return;
    try {
      const res = await userRepo.deleteUserDevice(userId, token);
      if (!res.status) {
        setMessage({ type: "error", message: res.message || "حذف دستگاه ناموفق بود." });
        return;
      }
      setMessage({ type: "success", message: "دستگاه حذف شد." });
      await reloadUser();
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    }
  };

  if (loading || !user || !form) {
    return (
      <section className="space-y-4">
        <AdminPageTitle title="جزئیات کاربر" />
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">در حال دریافت اطلاعات...</div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <AdminPageTitle title={user.name || user.username} />
        <Link
          to="/admin/users"
          className="inline-flex rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
        >
          بازگشت به لیست
        </Link>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          <h3 className="mb-4 text-sm font-semibold text-slate-200">حساب کاربری</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2 md:col-span-2">
              <span className="text-xs text-slate-400">نام کاربری</span>
              <input readOnly className={`${inputClass} cursor-not-allowed opacity-70`} value={user.username} />
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">نام نمایشی</span>
              <input
                className={inputClass}
                value={form.name}
                onChange={(e) => setForm((f) => (f ? { ...f, name: e.target.value } : f))}
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">نقش</span>
              <select
                className={inputClass}
                value={form.role}
                onChange={(e) => setForm((f) => (f ? { ...f, role: e.target.value } : f))}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">ایمیل</span>
              <input
                type="email"
                className={inputClass}
                value={form.email}
                onChange={(e) => setForm((f) => (f ? { ...f, email: e.target.value } : f))}
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">تلفن</span>
              <input
                className={inputClass}
                value={form.phone}
                onChange={(e) => setForm((f) => (f ? { ...f, phone: e.target.value } : f))}
              />
            </label>
            <label className="space-y-2 md:col-span-2">
              <span className="text-xs text-slate-400">رمز عبور جدید (خالی بگذارید اگر نمی‌خواهید عوض شود)</span>
              <input
                type="password"
                autoComplete="new-password"
                className={inputClass}
                value={form.password}
                onChange={(e) => setForm((f) => (f ? { ...f, password: e.target.value } : f))}
                placeholder="••••••••"
              />
            </label>
            <label className="flex flex-col space-y-2">
              <span className="text-xs text-slate-400">شروع VIP</span>
              <DatePicker
                value={vipStartAt}
                onChange={(value) => {
                  const d = (value as DateObject) || null;
                  setVipStartAt(d);
                  setForm((f) =>
                    f ? { ...f, vip_start_date: d ? toTehranDateTimeString(d) : "" } : f
                  );
                }}
                calendar={persian}
                locale={persian_fa}
                plugins={[<TimePicker key="time-picker" position="bottom" />]}
                format="YYYY/MM/DD HH:mm:ss"
                calendarPosition="bottom-right"
                inputClass={inputClass}
              />
            </label>
            <label className="flex flex-col space-y-2">
              <span className="text-xs text-slate-400">پایان VIP</span>
              <DatePicker
                value={vipEndAt}
                onChange={(value) => {
                  const d = (value as DateObject) || null;
                  setVipEndAt(d);
                  setForm((f) => (f ? { ...f, vip_end_date: d ? toTehranDateTimeString(d) : "" } : f));
                }}
                calendar={persian}
                plugins={[<TimePicker key="time-picker" position="bottom" />]}
                locale={persian_fa}
                format="YYYY/MM/DD HH:mm:ss"
                calendarPosition="bottom-right"
                inputClass={inputClass}
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">امتیاز</span>
              <input
                type="number"
                className={inputClass}
                value={form.score}
                onChange={(e) =>
                  setForm((f) => (f ? { ...f, score: e.target.value === "" ? 0 : Number(e.target.value) } : f))
                }
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">امتیاز VIP</span>
              <input
                type="number"
                className={inputClass}
                value={form.vip_score}
                onChange={(e) =>
                  setForm((f) => (f ? { ...f, vip_score: e.target.value === "" ? 0 : Number(e.target.value) } : f))
                }
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">سطح</span>
              <input
                type="number"
                className={inputClass}
                value={form.level}
                onChange={(e) =>
                  setForm((f) => (f ? { ...f, level: e.target.value === "" ? 0 : Number(e.target.value) } : f))
                }
              />
            </label>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          <h3 className="mb-4 text-sm font-semibold text-slate-200">دستگاه‌های فعال</h3>
          {!user.user_device_data.length ? (
            <p className="text-sm text-slate-500">دستگاهی ثبت نشده است.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-800">
              <table className="min-w-full divide-y divide-slate-800 text-sm">
                <thead className="bg-slate-900/80">
                  <tr>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">نوع</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">نام</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">ورود</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">عملیات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {user.user_device_data.map((d, idx) => (
                    <tr key={`${d.device_token || "t"}-${idx}`} className="hover:bg-slate-900/60">
                      <td className="whitespace-nowrap px-3 py-2 text-slate-300">{d.device_type || "—"}</td>
                      <td className="px-3 py-2 text-slate-200">{d.device_name || "—"}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-400">{d.login_at || "—"}</td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => void onDeleteDevice(d.device_token)}
                          className="rounded-lg border border-rose-800 px-2 py-1 text-xs text-rose-200 hover:border-rose-600"
                        >
                          حذف دستگاه
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "در حال ذخیره…" : "ذخیره تغییرات"}
          </button>
          <button
            type="button"
            onClick={() => void onDeleteUser()}
            className="rounded-lg border border-rose-800 px-5 py-2 text-sm text-rose-200 transition hover:border-rose-600"
          >
            حذف کاربر
          </button>
        </div>
      </form>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
        <h3 className="mb-2 text-sm font-semibold text-slate-200">ارسال اعلان</h3>
        <p className="mb-4 text-xs text-slate-400">
          این اعلان فقط برای {user.name || user.username} ارسال می‌شود.
        </p>
        <NotificationSendForm userId={userId} submitLabel="ارسال اعلان به کاربر" />
      </div>
    </section>
  );
}
